/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1776729600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Tue Apr 21 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "sprig",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Sprig",
          "depth": 0,
          "id": "connect-to-sprig"
        }, {
          "value": "API key",
          "depth": 1,
          "id": "api-key"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing users",
          "depth": 1,
          "id": "syncing-users"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Rate limits",
          "depth": 1,
          "id": "rate-limits"
        }, {
          "value": "Environment mismatch",
          "depth": 1,
          "id": "environment-mismatch"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      strong: "strong",
      hr: "hr",
      img: "img",
      h3: "h3",
      ol: "ol",
      li: "li",
      h4: "h4",
      ul: "ul",
      code: "code"
    }, _provideComponents(), props.components), {CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Sprig is a product experience platform that helps teams collect in-context user feedback through surveys, session replays, heatmaps, and AI-powered analysis. Use Hightouch to sync user attributes from your data warehouse to Sprig so you can target studies to the right audiences, filter responses by user properties, and keep your Sprig user profiles enriched with up-to-date data."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported sync modes"
            }), _jsx(_components.th, {
              children: "API reference"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#syncing-users",
                children: _jsx(_components.strong, {
                  children: "Users"
                })
              })
            }), _jsx(_components.td, {
              children: "Sync user profiles and attributes to Sprig"
            }), _jsx(_components.td, {
              children: "Upsert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://docs.sprig.com/reference/post-user-v2-1",
                children: "V2 Users API"
              })
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "connect-to-sprig",
        children: "Connect to Sprig"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Sprig"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/sprig/sprig-create-destination.png",
          alt: "Selecting Sprig in the Hightouch destination catalog"
        })
      }), "\n", _jsx(_components.h3, {
        id: "api-key",
        children: "API key"
      }), "\n", _jsxs(_components.p, {
        children: ["To authenticate, you need a Sprig API key. Sprig uses the ", _jsx(_components.a, {
          href: "https://docs.sprig.com/docs/apis",
          children: "Data Import API"
        }), " key for server-side integrations."]
      }), "\n", _jsx(_components.p, {
        children: "To find your API key in Sprig:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In the Sprig navigation pane, click ", _jsx(_components.strong, {
            children: "Integrations"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Sources"
          }), ", click ", _jsx(_components.strong, {
            children: "Enrichment"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Data Import API"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Copy the API key for the environment you want to sync to (Production or Development)."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/sprig/sprig-data-import-api.png",
          alt: "Locating the Data Import API in Sprig's Integrations page"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Paste your API key into the ", _jsx(_components.strong, {
          children: "API Key"
        }), " field in Hightouch and click ", _jsx(_components.strong, {
          children: "Test connection"
        }), " to verify the credentials."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/sprig/sprig-test-connection.png",
          alt: "Entering the Sprig API key in Hightouch and testing the connection"
        })
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["Sprig provides separate API keys for Production and Development environments.\nMake sure you use the key that matches the environment you want to sync data to.\nRefer to the ", _jsx(_components.a, {
            href: "https://docs.sprig.com/docs/apis#find-your-api-keys",
            children: "Sprig API documentation"
          }), " for details."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Configure any default sync alerts for this destination, then click ", _jsx(_components.strong, {
          children: "Continue"
        }), " to finalize."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/sprig/sprig-configure-alerts.png",
          alt: "Configuring default alerts for the Sprig destination"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Sprig destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Sprig destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-users",
        children: "Syncing users"
      }), "\n", _jsxs(_components.p, {
        children: ["Sync user profiles and attributes to Sprig. Hightouch uses the ", _jsx(_components.a, {
          href: "https://docs.sprig.com/reference/post-user-v2-1",
          children: "Sprig V2 Users API"
        }), " to upsert user records. When a matching user exists in Sprig, Hightouch updates their attributes. When no match is found, Hightouch creates a new user profile."]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["To match rows from your model to users in Sprig, select a model column to map to the Sprig ", _jsx(_components.strong, {
          children: "User ID"
        }), " field. This is the unique identifier Sprig uses to track individual users across your product."]
      }), "\n", _jsxs(_components.p, {
        children: ["The User ID should be a stable, unique identifier for each user, such as an internal user ID or database primary key. This must match the User ID you use in your ", _jsx(_components.a, {
          href: "https://docs.sprig.com/docs/web-javascript",
          children: "Sprig SDK installation"
        }), " so that synced attributes are associated with the correct user profiles."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you sync user attributes via ", _jsx(_components.a, {
          href: "/syncs/mapping-data",
          children: "field mapping"
        }), ". You can map data from any of your model columns to Sprig user attributes."]
      }), "\n", _jsx(_components.p, {
        children: "Sprig supports attributes with string, numeric, and boolean values. Attributes synced through Hightouch appear in Sprig and can be used to:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Filter study audiences"
          }), ": Target surveys and feedback prompts to users with specific attribute values."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Filter study responses"
          }), ": Drill into collected responses based on user attributes."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Any attribute key you sync that doesn't already exist in Sprig is created automatically. Refer to the ", _jsx(_components.a, {
          href: "https://docs.sprig.com/docs/attributes",
          children: "Sprig Attributes documentation"
        }), " for more details on how attributes work."]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Sprig does not support deleting individual attributes from a user profile.\nIf you sync an attribute in error, you can overwrite its value but you cannot remove the key entirely unless you delete the user's data."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "rate-limits",
        children: "Rate limits"
      }), "\n", _jsxs(_components.p, {
        children: ["Enterprise Sprig customers have an API rate limit of 1,000 requests per second. If you're on a Starter or Free plan, API access may be limited. Refer to the ", _jsx(_components.a, {
          href: "https://docs.sprig.com/docs/apis#rate-limiting",
          children: "Sprig API documentation"
        }), " for current rate limit details."]
      }), "\n", _jsx(_components.h3, {
        id: "environment-mismatch",
        children: "Environment mismatch"
      }), "\n", _jsx(_components.p, {
        children: "Make sure the API key you use in Hightouch corresponds to the correct Sprig environment (Production or Development). Using a Development key will sync data to your Development environment, which won't affect your live studies or production user profiles."
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Error"
            }), _jsx(_components.th, {
              children: "Cause"
            }), _jsx(_components.th, {
              children: "Fix"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "401 Unauthorized"
              })
            }), _jsx(_components.td, {
              children: "The API key is invalid or has been revoked."
            }), _jsxs(_components.td, {
              children: ["Generate a new API key in Sprig under ", _jsx(_components.strong, {
                children: "Integrations > Enrichment > Data Import API"
              }), " and update the destination in Hightouch."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "429 Too Many Requests"
              })
            }), _jsx(_components.td, {
              children: "You've exceeded the Sprig API rate limit."
            }), _jsx(_components.td, {
              children: "Reduce sync frequency or contact Sprig to increase your rate limit."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
